import React, { useState, useEffect } from "react";
import FacebookIcon from "../assets/vector/FacebookIcon";
import InstagramIcon from "../assets/vector/InstagramIcon";
import TwitterIcon from "../assets/vector/TwitterIcon";
import { Facebook, Instagram, Twitter, LogIn } from "lucide-react";
import * as XLSX from "xlsx";
import ConsoleAnalysis from "./ConsoleAnalysis";
import {
  TwitterCrawlerService,
  SentimentAnalyzerService,
} from "../services/AnalysisService";
import SentimentAnalysis from "./SentimentAnalysis";
import { useFacebookData } from "./SocialMediaData";
import TweetTable from "./TwitterAnalysis/TweetTable";

const SocialMediaAnalysis = () => {
  const [socialMediaLink, setSocialMediaLink] = useState("");
  const [detectedPlatform, setDetectedPlatform] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [backgroundPosition, setBackgroundPosition] = useState("0% 50%");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [inputText, setInputText] = useState("");
  const [sentimentResults, setSentimentResults] = useState([]);
  const [consoleResults, setConsoleResults] = useState([]);

  const [tweetData, setTweetData] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [error, setError] = useState("");
  const [analysisResult, setAnalysisResult] = useState([]);
  const [sourceType, setSourceType] = useState(null);
  const [userInput, setUserInput] = useState("");
  const [username, setUsername] = useState("");
  const [inputLink, setInputLink] = useState("");
  const [inputKeyword, setInputKeyword] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [numTweets, setNumTweets] = useState(20);
  const [sentimentCounts, setSentimentCounts] = useState({
    negative: 0,
    neutral: 0,
    positive: 0,
  });
  const [sentimentTotal, setSentimentTotal] = useState(0);
  const [textId, setTextId] = useState([]);
  const [textContent, setTextContent] = useState([]);
  const [sentimentArray, setSentimentArray] = useState([]);
  const [showTweetLimit, setShowTweetLimit] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const tweetsPerPage = 10;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastTweet = currentPage * tweetsPerPage;
  const indexOfFirstTweet = indexOfLastTweet - tweetsPerPage;
  const currentTweets = tweetData.slice(indexOfFirstTweet, indexOfLastTweet);

  const totalPages = Math.ceil(tweetData.length / tweetsPerPage);

  /**
   * FACEBOOK: WORK IN PROGRESS
   */
  const page_id = "103127515742819";
  const post_id = "362265946498193";
  const access_token =
    "EAA2sFN7aRf4BO5a3cN3li24qzw1GeBuvpx7KZANh46lWlS0MUxlZAJykb7kKVSEcXum6wxXyM08aIbJKc93ZAU1QDA1MMXNZADmHBdPWUxCxPH0701eEsgy8Cge3sPsVJeOwQ9zS15ZBuH8G1t12LQLlZCqSNCmOo7AVLLDBfaDMjVqezCZAZA2XHEtrcUU4h84ZD";

  const {
    data,
    comments,
    commentsIDs,
    error: facebookError,
  } = useFacebookData(page_id, post_id, access_token);

  // Handle Facebook error
  useEffect(() => {
    if (facebookError) {
      setError(facebookError);
    }
  }, [facebookError]);

  /**
   * TWITTER or X: USE TWEET HARVEST TOOL
   * There will be some adjustments if we choose to move to
   * X official API
   */
  const crawlTweets = async (query) => {
    if (query.trim() === "") {
      setError("Link cannot be empty");
      setTimeout(() => setError(""), 3000);
      return;
    }
    // console.log("query: " + query);
    // console.log("sourceType: " + sourceType);
    // console.log("numTweets: " + numTweets);
    const tweetLink = sourceType === "link" ? "true" : "false";
    setError("Fetching tweets...");
    try {
      setIsFetching(true);
      console.log("tweetLink: " + tweetLink);
      console.log("numTweets: " + numTweets);
      console.log("query: " + query);
      const result = await TwitterCrawlerService(tweetLink, numTweets, query);
      if (result) {
        setTweetData(result);
        const newProcessedData = result.map((tweet) => ({
          id: tweet.conversation_id_str,
          content: tweet.full_text,
        }));
        setProcessedData(newProcessedData);
        console.log(newProcessedData);
        console.log(result);
        setError("");
        setInputText(userInput);
        setIsFetching(false);
      } else {
        setError("Looks like we're experiencing trouble. Try refreshing!");
        setInputText(userInput);
        setIsFetching(false);
      }
    } catch (error) {
      setError(error.message);
      setIsFetching(false);
    }
  };

  // Remove tweet from crawled data
  const removeTweet = (index) => {
    const updatedTweetData = tweetData.filter((_, i) => i !== index);
    setTweetData(updatedTweetData);
    const updatedProcessedData = processedData.filter((_, i) => i !== index);
    setProcessedData(updatedProcessedData);
    console.log(updatedProcessedData);
  };

  /**
   * Analyze the tweets data
   * both for CONSOLE and sentiment analysis
   * @returns: An array contains the tweet id, tweet content,
   * CONSOLE result, and sentiment analysis result.
   * Each tweet represent each element.
   */
  const analyzeSentiment = async () => {
    if (processedData.length === 0) {
      setError("No tweets to analyse. Please fetch tweets first.");
      setTimeout(() => setError(""), 3000);
      return;
    }
    setIsAnalyzing(true);
    setError("Analysing sentiment...");
    try {
      const result = await SentimentAnalyzerService(processedData);
      if (result) {
        console.log("Sentiment analysis result:", result);
        console.log("Analysis: ", result);
        setAnalysisResult(result);

        // Create an array to store the highest sentiment category for each item
        const sentimentArray = result.map((item) => {
          const { negative, neutral, positive } = item.sentimentResult;

          // Determine the sentiment with the highest score
          const maxSentiment = Math.max(negative, neutral, positive);

          if (maxSentiment === neutral) return "neutral";
          if (maxSentiment === positive) return "positive";
          return "negative";
        });

        setSentimentArray(sentimentArray);

        /**
         * DATA MANIPULATION
         */
        let negCount = 0;
        let neuCount = 0;
        let posCount = 0;
        let total = 0;
        result.forEach((result) => {
          const { negative, neutral, positive } = result.sentimentResult;
          if (negative > neutral && negative > positive) {
            negCount += 1;
          } else if (neutral > negative && neutral > positive) {
            neuCount += 1;
          } else if (positive > negative && positive > neutral) {
            posCount += 1;
          }
          total += 1;
        });
        setSentimentCounts((prevCounts) => ({
          negative: prevCounts.negative + negCount,
          neutral: prevCounts.neutral + neuCount,
          positive: prevCounts.positive + posCount,
        }));
        setSentimentTotal(total);
        console.log("Check: ", sentimentCounts);
        console.log("Check: ", sentimentTotal);
        /**
         * DATA MANIPULATION: PART 2
         */
        let finalResultArray = [];
        let textIdArray = [];
        let textContentArray = [];
        result.forEach((result) => {
          finalResultArray.push(result.consoleResult);
          textIdArray.push(result.textId);
          textContentArray.push(result.textContent);
        });
        setConsoleResults(finalResultArray);
        setTextId(textIdArray);
        setTextContent(textContentArray);
        /**
         * END
         */

        setError("");
      } else {
        setError("Unable to analyse sentiment. Please try again later.");
      }
    } catch (error) {
      console.error("Error analysing sentiment:", error);
      setError(
        "An error occurred while analyzing sentiment. Please try again."
      );
    }
    setIsAnalyzing(false);
  };

  useEffect(() => {
    const detectPlatform = () => {
      const link = socialMediaLink.toLowerCase();
      if (link.includes("facebook.com")) {
        setDetectedPlatform("facebook");
        setBackgroundPosition("0% 0%");
      } else if (link.includes("instagram.com")) {
        setDetectedPlatform("instagram");
        setBackgroundPosition("50% 50%");
      } else if (link.includes("twitter.com") || link.includes("x.com")) {
        setDetectedPlatform("twitter");
        setBackgroundPosition("100% 100%");
        extractUsername(link);
      } else {
        setDetectedPlatform("");
        setBackgroundPosition("0% 50%");
      }
    };

    const extractUsername = (link) => {
      const regex = /(?:twitter\.com|x\.com)\/([a-zA-Z0-9_]+)/;
      const match = link.match(regex);
      if (match && match[1]) {
        setUsername(match[1]);
      } else {
        setUsername("");
      }
    };

    detectPlatform();
  }, [socialMediaLink]);

  const getLoginIcon = () => {
    switch (detectedPlatform) {
      case "facebook":
        return <Facebook size={20} />;
      case "instagram":
        return <Instagram size={20} />;
      case "twitter":
        return <Twitter size={20} />;
      default:
        return <LogIn size={20} />;
    }
  };

  console.log("Sentiment Array: ", sentimentArray);

  const handleSocialMedia = () => {
    switch (detectedPlatform) {
      case "twitter":
        if (sourceType != "link") {
          let fullQuery = inputKeyword;
          if (username != "" && endDate != "" && startDate != "") {
            fullQuery +=
              " (from:" +
              username +
              ") until:" +
              endDate +
              " since:" +
              startDate;
          } else if (username != "" && endDate == "" && startDate == "") {
            fullQuery += " (from:" + username + ")";
          } else if (username == "" && endDate != "" && startDate != "") {
            fullQuery += " until:" + endDate + " since:" + startDate;
          } else if (username != "" && endDate != "" && startDate == "") {
            fullQuery += " (from:" + username + ")";
          }
          crawlTweets(fullQuery);
        } else {
          crawlTweets(inputLink);
        }
        break;
      default:
        break;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const day = days[date.getDay()];
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");

    return `${day} ${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  // CSV Data Export
  const convertToCSV = (data) => {
    const array = [Object.keys(data[0])].concat(data);

    return array
      .map((row) => {
        return Object.values(row)
          .map((value) =>
            typeof value === "string" ? JSON.stringify(value) : value
          )
          .toString();
      })
      .join("\n");
  };

  const downloadCSV = (data, filename) => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.setAttribute("hidden", "");
    a.setAttribute("href", url);
    a.setAttribute("download", filename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Function to export Tweet Table data
  const exportTweetTableData = () => {
    const tweetTableData = tweetData.map((tweet) => {
      const date = new Date(tweet.created_at);
      const formattedDate = date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

      return {
        username: tweet.username,
        full_text: tweet.full_text,
        created_at: formattedDate,
        tweet_link: tweet.tweet_url,
      };
    });

    const tweetHeaders = Object.keys(tweetTableData[0] || {});
    const headerRow = tweetHeaders.join(",");
    const dataRows = tweetTableData.map((row) =>
      Object.values(row)
        .map((value) =>
          typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
        )
        .join(",")
    );

    const csv = [headerRow, ...dataRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("hidden", "");
    link.setAttribute("href", url);
    link.setAttribute("download", "tweet_table_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to export Sentiment Analysis and Console Analysis data
  const exportAnalysisData = () => {
    const sentimentData = textContent.map((comment, index) => ({
      comment,
      sentiment: sentimentArray[index],
    }));

    const consoleData = textContent.map((comment, index) => {
      const numericValues = consoleResults[index][0].numericValues;
      const score = consoleResults[index][0].score;

      return {
        comment,
        ...numericValues,
        aggregated_score: score,
      };
    });

    // Create worksheet for Sentiment Analysis
    const sentimentSheet = XLSX.utils.json_to_sheet(sentimentData);

    // Create worksheet for Console Analysis
    const consoleSheet = XLSX.utils.json_to_sheet(consoleData);

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Append sheets to the workbook
    XLSX.utils.book_append_sheet(
      workbook,
      sentimentSheet,
      "Sentiment Analysis"
    );
    XLSX.utils.book_append_sheet(workbook, consoleSheet, "Console Analysis");

    // Write the workbook to a file
    XLSX.writeFile(workbook, "analysis_data.xlsx");
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8">
      <div className="mx-auto mt-8 max-w-7xl">
        <div className="p-4 bg-white rounded-lg shadow-md sm:p-6">
          <div className="mb-4">
            <label
              htmlFor="socialMediaLink"
              className="block mb-2 text-lg font-semibold text-gray-700"
            >
              Social Media Profile Link<span className="text-red-500">*</span>
            </label>
            <div className="flex flex-col sm:flex-row">
              <input
                type="text"
                id="socialMediaLink"
                className="flex-grow w-full px-4 py-2 mb-2 border border-gray-300 rounded-md sm:w-auto sm:rounded-l-md sm:rounded-r-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:mb-0"
                placeholder="Enter your social media link (e.g., https://twitter.com/username)"
                value={socialMediaLink}
                onChange={(e) => setSocialMediaLink(e.target.value)}
              />
              {!isLoggedIn && detectedPlatform && (
                <button
                  className="flex items-center justify-center w-full px-4 py-2 font-semibold text-white transition-all duration-300 ease-in-out rounded-md sm:w-auto sm:rounded-l-none sm:rounded-r-md"
                  style={{
                    background: "rgb(55,113,228)",
                    backgroundImage:
                      "linear-gradient(149deg, rgba(55,113,228,1) 24%, rgba(115,0,255,1) 39%, rgba(255,0,207,1) 47%, rgba(255,184,0,1) 66%, rgba(72,148,226,1) 76%)",
                    backgroundSize: "600% 600%",
                    backgroundPosition: backgroundPosition,
                  }}
                  onClick={handleSocialMedia}
                  disabled={true} // Just disabled it for now
                >
                  {getLoginIcon()}
                  <span className="ml-2">Login</span>
                </button>
              )}
            </div>
          </div>
          {detectedPlatform === "twitter" && (
            <div className="mb-4">
              <div className="grid grid-cols-1">
                {/* Source Type Toggle Buttons */}
                <div className="mb-4">
                  <label className="block mb-2 text-lg font-semibold text-gray-700">
                    Fetch by
                  </label>
                  <div className="flex">
                    <button
                      type="button"
                      className={`px-4 py-2 border border-gray-300 rounded-l-md w-full duration-200 focus:ring-orange-500 ${
                        sourceType === null
                          ? "bg-orange-500 text-white"
                          : "bg-white text-gray-700 hover:bg-orange-200"
                      }`}
                      onClick={() => setSourceType(null)}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`px-4 py-2 border border-gray-300 w-full duration-200 focus:ring-orange-500 ${
                        sourceType === "link"
                          ? "bg-orange-500 text-white"
                          : "bg-white text-gray-700 hover:bg-orange-200"
                      }`}
                      onClick={() => setSourceType("link")}
                    >
                      Tweet Link
                    </button>
                    <button
                      type="button"
                      className={`px-4 py-2 border border-gray-300 w-full rounded-r-md duration-200 focus:ring-orange-500 ${
                        sourceType === "keyword"
                          ? "bg-orange-500 text-white"
                          : "bg-white text-gray-700 hover:bg-orange-200"
                      }`}
                      onClick={() => setSourceType("keyword")}
                    >
                      Keyword
                    </button>
                  </div>
                </div>

                {/* Conditionally Rendered Input Fields */}
                {sourceType === "link" && (
                  <div className="col-span-2 mb-4">
                    <label
                      htmlFor="linkInput"
                      className="block mb-2 text-lg font-semibold text-gray-700"
                    >
                      Tweet Link<span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="linkInput"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder={`Enter Tweet Link (eg., https://twitter.com/${username}/status/1850571438446522815`}
                      value={inputLink}
                      onChange={(e) => {
                        setInputLink(e.target.value);
                      }}
                    />
                  </div>
                )}

                {sourceType === "keyword" && (
                  <div className="col-span-2 mb-4">
                    <label
                      htmlFor="keywordInput"
                      className="block mb-2 text-lg font-semibold text-gray-700"
                    >
                      Keyword
                    </label>
                    <input
                      type="text"
                      id="keywordInput"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="eg., Artwork, News, Project"
                      value={inputKeyword}
                      onChange={(e) => {
                        setInputKeyword(e.target.value);
                      }}
                    />
                  </div>
                )}

                <div className="col-span-2">
                  <label className="flex mb-3 mt-2 items-center space-x-2 text-gray-700">
                    <input
                      type="checkbox"
                      checked={showTweetLimit}
                      onChange={(e) => setShowTweetLimit(e.target.checked)}
                      className="form-checkbox h-4 w-4"
                    />
                    <span>Limit tweets</span>
                  </label>

                  {showTweetLimit && (
                    <>
                      <label
                        htmlFor="numTweetsInput"
                        className="block mb-2 text-lg font-semibold text-gray-700"
                      >
                        Number of Tweets
                        <span className="text-sm text-gray-500"></span>
                      </label>

                      <input
                        type="number"
                        id="numTweetsInput"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Enter the number of tweets to crawl."
                        value={numTweets}
                        onChange={(e) => {
                          setNumTweets(e.target.value);
                        }}
                      />
                      <p className="mt-2 text-sm text-gray-600">
                        The number of tweets is an approximate value. Small
                        differences between numbers (e.g., 6 and 10) might not
                        be detected as distinct. Try entering values like 10,
                        20, 100, or 200 to retrieve tweets. If tweets exist but
                        nothing is crawled, try adjusting the number of
                        tweets—it sometimes resolves the issue.
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="flex flex-col items-center justify-between md:flex-row">
            <div className="flex justify-start mb-4 sm:mb-0">
              <div
                className={`px-1 transition-opacity duration-300 ${
                  detectedPlatform === "facebook" ? "opacity-100" : "opacity-50"
                }`}
              >
                <FacebookIcon />
              </div>
              <div
                className={`px-1 transition-opacity duration-300 ${
                  detectedPlatform === "instagram"
                    ? "opacity-100"
                    : "opacity-50"
                }`}
              >
                <InstagramIcon />
              </div>
              <div
                className={`px-1 transition-opacity duration-300 ${
                  detectedPlatform === "twitter" ? "opacity-100" : "opacity-50"
                }`}
              >
                <TwitterIcon />
              </div>
            </div>
            <div className="flex flex-col items-center justify-between gap-3 mt-4 mb-4 md:flex-row">
              <span className="mx-3 font-medium text-gray-600">From</span>
              <div className="flex items-center mb-4 sm:mb-0">
                <input
                  type="date"
                  className="px-3 py-2 text-gray-700 transition-all duration-300 ease-in-out border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <span className="mx-3 font-medium text-gray-600">To</span>
              <div className="flex items-center mb-4 sm:mb-0">
                <input
                  type="date"
                  className="px-3 py-2 text-gray-700 transition-all duration-300 ease-in-out border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <button
                className={`ml-3 px-5 py-2 font-semibold rounded-md transition-colors duration-300 text-white ease-in-out bg-orange-500 hover:bg-orange-600`}
                onClick={handleSocialMedia}
                disabled={isFetching}
              >
                {isFetching ? "Fetching..." : "Fetch"}
              </button>
            </div>
          </div>
        </div>

        {/* List of Fetched Tweets */}
        <div>
          <TweetTable
            exportTweetTableData={exportTweetTableData}
            tweetData={tweetData}
            isAnalyzing={isAnalyzing}
            processedData={processedData}
            analyzeSentiment={analyzeSentiment}
            currentTweets={currentTweets}
            formatDate={formatDate}
            removeTweet={removeTweet}
          />
        </div>

        {/* Pagination */}
        <div className="flex justify-center mt-4">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => handlePageChange(i + 1)}
              className={`px-4 py-2 mx-1 border rounded ${
                currentPage === i + 1
                  ? "bg-orange-600 text-white"
                  : "bg-white orange"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {/* Show the sentiment result */}
        <div className="mt-4">
          {sentimentTotal > 0 && (
            <SentimentAnalysis
              commentsCount={sentimentTotal}
              counts={sentimentCounts}
              comments={textContent}
              sentimentArray={sentimentArray}
            />
          )}
        </div>

        {/* Show the console result */}
        <div>
          <ConsoleAnalysis
            comments={textContent}
            error={error}
            analysisResult={sentimentTotal > 0 ? consoleResults : null}
            messageIDs={textId}
            exportAnalysisData={exportAnalysisData}
          />
        </div>
      </div>
    </div>
  );
};

export default SocialMediaAnalysis;
